// utils/useForm.ts
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Validator = (value: any) => string | null;
type FormData = Record<string, any>;
type ValidationMap = Record<string, Validator[]>;
type Appearance = 'danger' | 'none' | 'success' | 'warning' | undefined;
type HTMLElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type OnChangeEvent =
  | React.ChangeEvent<HTMLElements>
  | React.FormEvent<HTMLElement>;

const required =
  (msg = 'FormValidator.InvalidFieldRequired'): Validator =>
  (v) =>
    v ? null : msg;

const minLength =
  (min: number, msg?: string): Validator =>
  (v) =>
    v?.length >= min ? null : msg || 'FormValidator.InvalidMinCaracters';

const validCountryCodes = new Set([
  '+55',
  '+54',
  '+52',
  '+56',
  '+57',
  '+51',
  '+58',
  '+591',
  '+595',
  '+598',
  '+1',
  '+351',
  '+34',
]);

const isCountryPhoneCode =
  (msg = 'FormValidator.InvalidCountryPhoneCode'): Validator =>
  (value) => {
    return validCountryCodes.has(value) ? null : msg;
  };

export const isPhoneByCountry =
  (
    getCountryCode: () => string | undefined,
    msg = 'FormValidator.InvalidPhone',
  ): Validator =>
  (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    const country = getCountryCode();
    console.log(country, digits);

    if (!digits || !country) return msg;

    switch (country) {
      case '+55': // 🇧🇷 Brasil (11 dígitos, começa com 9)
        return /^\d{11}$/.test(digits) && digits[2] === '9' ? null : msg;

      case '+54': // 🇦🇷 Argentina (normalmente 10 ou 11)
        return /^\d{10,11}$/.test(digits) ? null : msg;

      case '+52': // 🇲🇽 México (10 fixos)
        return /^\d{10}$/.test(digits) ? null : msg;

      case '+56': // 🇨🇱 Chile (9 fixos)
        return /^\d{9}$/.test(digits) ? null : msg;

      default: // Outros países
        return digits.length >= 8 && digits.length <= 15 ? null : msg;
    }
  };

export const formValidators = {
  required,
  minLength,
  isPhoneByCountry,
  isCountryPhoneCode,
};

export const useForm = <T extends FormData>(initialData: T) => {
  const [form, setForm] = useState<T>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validations, setValidations] = useState<ValidationMap>({});

  const { t } = useTranslation();

  const getValue = (name: string): any => {
    return name.split('.').reduce((obj, key) => obj?.[key], form) as any;
  };

  const getFieldProps = (
    name: string,
    is_toggle: boolean,
  ): {
    name: string;
    id: string;
    value?: string | number | readonly string[] | undefined;
    checked?: boolean;
    onChange: (e: OnChangeEvent) => void;
    appearance?: Appearance;
    crossOrigin: '';
  } => {
    return is_toggle
      ? {
          name,
          id: name,
          checked: getValue(name) as boolean,
          onChange: handleChange,
          crossOrigin: '',
        }
      : {
          name,
          id: name,
          value: getValue(name),
          onChange: handleChange,
          appearance: errors[name] ? 'danger' : 'none',
          crossOrigin: '',
        };
  };

  const handleChange = (e: OnChangeEvent) => {
    const target = e.target as HTMLElements;
    const { name, type, value } = target;

    if (!name) return; // ignora se não tiver name

    const isCheckbox = type === 'checkbox' || type === 'radio';
    const finalValue = isCheckbox
      ? (target as HTMLInputElement).checked
      : value;

    setForm((prev) => {
      const newForm = { ...prev };
      const keys = name.split('.');
      const lastKey = keys.pop()!;

      let ref: any = newForm;
      for (const key of keys) {
        if (!ref[key]) ref[key] = {};
        ref = ref[key];
      }

      console.log(JSON.stringify(ref, null, 2), lastKey, finalValue);
      ref[lastKey] = finalValue;

      return { ...newForm };
    });

    // ✅ Validação imediata do campo alterado APENAS se já estiver com erro
    if (validations[name] && errors[name]) {
      const valueToValidate = isCheckbox
        ? (target as HTMLInputElement).checked
        : value;

      for (const rule of validations[name]) {
        const error = rule(valueToValidate);

        setErrors((prev) => {
          const newErrors = { ...prev };

          if (error) {
            newErrors[name] = t(error);
          } else {
            delete newErrors[name];
          }

          return newErrors;
        });

        if (error) break;
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    for (const field in validations) {
      const value = field.split('.').reduce((obj, k) => obj?.[k], form);
      for (const rule of validations[field]) {
        const error = rule(value);
        if (error) {
          newErrors[field] = t(error);
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reset = () => {
    setForm(initialData);
    setErrors({});
  };

  return {
    form,
    setForm,
    getFieldProps,
    errors,
    handleChange,
    validate,
    setValidations,
    reset,
  };
};
